import React, { useState, useEffect } from 'react';
import {
    BrowserRouter as Router,
    Route,
    Routes as Switch,
    Navigate,
} from "react-router-dom";
import "./App.css";
import HomePage from "./pages/home";
import EmployeeProfilePage from "./pages/employee-profile";
import HrProfilePage from "./pages/hr-profile";
import RegistrationPage from "./pages/registration"
import LoginPage from "./pages/login";
import ResetPasswordPage from "./pages/reset-password";
import ActivatePage from "./pages/activate";
import GrabShiftPage from "./pages/grab-shift";
import MyShiftsPage from "./pages/my-shifts";
import ApproveRejectPage from "./pages/approve-reject";
import ReportsPage from "./pages/reports";
import WorkdaySyncReport from "./pages/workday-sync-report";
import SiteSelectPage from "./pages/site-select";
import Layout from './components/layout/Layout';
import AppInitializer from './components/AppInitializer';
import ProtectedRoute from './components/ProtectedRoute';
import ReactGA from "react-ga4";
import { env } from "./env";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import "@ace/ace-ui-web/dist/styles/styles.scss";
// Loader Context Provider
import { LoadingProvider } from "./components/Context/LoaderContext";
import SettingsPage from "./pages/settings/SettingsPage";
import { useUserRole } from './hooks/useUserRole';

ReactGA.initialize(`${env.REACT_APP_GA_MEASUREMENT_ID}`, {
    testMode: env.REACT_APP_GA_TEST_MODE,
});

function App() {
    // Sidebar state lives here at the top level.
    // Initialize collapsed on smaller viewports so tablets/phones open with the drawer closed.
    const [sidebarExpanded, setSidebarExpanded] = useState(
        typeof window === 'undefined' ? true : window.innerWidth >= 1024
    );

    // Auto-collapse sidebar on small screens
    useEffect(() => {
        const handleResize = () => {
            // Auto-collapse sidebar on screens smaller than 1024px
            if (window.innerWidth < 1024) {
                setSidebarExpanded(false);
            } else {
                setSidebarExpanded(true);
            }
        };

        // Run on mount
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Toggle function
    const handleToggle = () => {
        setSidebarExpanded(prevState => !prevState);
    };

    return (
        <LoadingProvider>
            <ErrorBoundary>
                <Router basename="/">
                    {/* Initialize user profile before rendering the app */}
                    <AppInitializer>
                        <Switch>
                            {/* Public Routes - no authentication required */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/registration" element={<RegistrationPage />} />
                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                            <Route path="/activate" element={<ActivatePage />} />
                            <Route path="/site-select" element={<SiteSelectPage />} />
                            {/* Protected Routes - require authentication */}
                            <Route path="/" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <HomePage />
                                    </Layout>
                                </ProtectedRoute>
                            } />

                            {/* Shift Management Routes */}
                            <Route path="/grab-shift" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <GrabShiftPage />
                                    </Layout>
                                </ProtectedRoute>
                            } />
                            <Route path="/my-shifts" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <MyShiftsPage />
                                    </Layout>
                                </ProtectedRoute>
                            } />
                            <Route path="/approve-reject" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <ApproveRejectPage />
                                    </Layout>
                                </ProtectedRoute>
                            } />

                            {/* Profile Routes */}
                            <Route path="/profile" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <EmployeeProfilePage />
                                    </Layout>
                                </ProtectedRoute>
                            } />
                            <Route path="/employee-profile" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <EmployeeProfilePage />
                                    </Layout>
                                </ProtectedRoute>
                            } />
                            <Route path="/hr-profile" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <HrProfilePage />
                                    </Layout>
                                </ProtectedRoute>
                            } />

                            {/* Reporting Routes */}
                            <Route path="/reports" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <ReportsPage />
                                    </Layout>
                                </ProtectedRoute>
                            } />
                            <Route path="/reports/workday-sync" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <WorkdaySyncReport />
                                    </Layout>
                                </ProtectedRoute>
                            } />

                            {/* Settings Routes - HR/Manager only */}
                            <Route path="/settings" element={
                                <ProtectedRoute>
                                    <Layout isExpanded={sidebarExpanded} onToggle={handleToggle}>
                                        <SettingsRouteGuard>
                                            <SettingsPage />
                                        </SettingsRouteGuard>
                                    </Layout>
                                </ProtectedRoute>
                            } />

                            {/* Redirect any unknown routes to home (which will redirect to login if not authenticated) */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Switch>
                    </AppInitializer>
                </Router>
            </ErrorBoundary>
        </LoadingProvider>
    );
}

export default App;

const SettingsRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isHROrManager, isLoading } = useUserRole();
    if (isLoading) return null; // or a spinner
    if (!isHROrManager) return <Navigate to="/" replace />;
    return <>{children}</>;
};